import React, { useRef, useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { ImagePlus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../api';
import { useBranding } from '../../context/BrandingContext';

export const BrandingForm = () => {
  const branding = useBranding();
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const upload = async (file) => {
    if (!file) return;
    if (!['image/png', 'image/jpeg', 'image/webp', 'image/svg+xml'].includes(file.type)) { toast.error('Use PNG, JPG, WEBP or SVG'); return; }
    if (file.size > 2 * 1024 * 1024) { toast.error('Logo must be under 2 MB'); return; }
    setBusy(true);
    try {
      const form = new FormData();
      form.append('file', file);
      await api.post('/admin/branding/logo', form, { headers: { 'Content-Type': 'multipart/form-data' } });
      branding.refresh();
      toast.success('Logo updated — it now shows in the header');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally { setBusy(false); if (inputRef.current) inputRef.current.value = ''; }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await api.delete('/admin/branding/logo');
      branding.refresh();
      toast.success('Logo removed — default badge restored');
    } catch { toast.error('Could not remove logo'); } finally { setBusy(false); }
  };

  return (
    <Card className="border-0 shadow-sm max-w-2xl" data-testid="branding-form">
      <CardContent className="p-6">
        <h3 className="text-lg font-bold text-gray-900 flex items-center mb-1"><ImagePlus className="w-5 h-5 mr-2 text-teal-600" />Agency logo</h3>
        <p className="text-sm text-gray-500 mb-5">Shown in the header next to the TripSoul name. Transparent PNG or SVG works best (max 2 MB, ~160×40 px).</p>
        <div className="flex items-center gap-6">
          <div className="h-20 w-48 rounded-xl border-2 border-dashed border-gray-200 bg-gray-50 flex items-center justify-center overflow-hidden" data-testid="logo-preview">
            {branding.logo_url ? <img src={branding.logo_url} alt="Current logo" className="max-h-16 max-w-[176px] object-contain" /> : <span className="text-xs text-gray-400">No logo uploaded</span>}
          </div>
          <div className="space-y-2">
            <input ref={inputRef} data-testid="logo-file-input" type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" className="hidden" onChange={(e) => upload(e.target.files?.[0])} />
            <Button data-testid="logo-upload-btn" disabled={busy} onClick={() => inputRef.current?.click()} className="bg-gradient-to-r from-teal-500 to-cyan-600 text-white"><ImagePlus className="w-4 h-4 mr-2" />{busy ? 'Uploading...' : branding.logo_url ? 'Replace logo' : 'Upload logo'}</Button>
            {branding.logo_url && <Button data-testid="logo-remove-btn" variant="ghost" disabled={busy} onClick={remove} className="text-red-600 hover:bg-red-50 w-full"><Trash2 className="w-4 h-4 mr-2" />Remove</Button>}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
